import React, { useCallback, useEffect, useRef, useState } from "react";

import { faChartPieSimple, faMemoPad, faPenToSquare } from "@fortawesome/pro-regular-svg-icons";
import { faCaretDown, faCaretUp } from "@fortawesome/pro-solid-svg-icons";

import { ButtonIcon, InputNumeric, Text, TextEditor, Tooltip } from "@performyard/py-components";
import type { ICellRendererParams, TextEditorRef } from "@performyard/py-components";
import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow, MyDecisionsDirectReportRow } from "../../types";
import { DecisionTableContext, IncreaseValues } from "../MyDecisions/types";

import { formatCurrencyFull } from "../../utils";

export type { DecisionRow };

const isDraftPercentMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const p = increaseValues[rowId]?.percent;
	return p === undefined || p === null || p === "";
};

const isDraftAmountMissing = (increaseValues: IncreaseValues, rowId: string): boolean => {
	const a = increaseValues[rowId]?.amount;
	return a === undefined || a === null || a === "";
};

const NOTES_MAX_LENGTH = 800;

/** Stable string for PATCH `notesByDecisionRowId` keys (direct-report `employee_id`) */
export const notesPayloadKey = (r: DecisionRow): string => String(r.employee_id ?? "");

export const toDecisionRow = (
	dr: MyDecisionsDirectReportRow,
	index: number,
	userID: string | undefined
): DecisionRow => {
	const approvalEntry =
		userID === undefined || dr.approvals.length === 0
			? null
			: (dr.approvals.find((a) => String(a.approver_id) === userID) ?? null);

	return {
		_id: dr._id,
		compensation_cycle_id: String(dr.compensation_cycle),
		employee_id: String(dr.employee_id),
		employee_name: dr.name || `Employee ${index + 1}`,
		employee_title: dr.jobTitle,
		current_salary: dr.current_salary,
		proposed_increase_percent: dr.proposed_increase_percent,
		proposed_increase_amount: dr.proposed_increase_amount,
		proposed_salary: dr.proposed_salary,
		status: dr.status,
		notes: approvalEntry?.notes ?? null,
		approver: approvalEntry?.approver_id != null ? String(approvalEntry.approver_id) : null,
		updatedAt: dr.updated_at ?? approvalEntry?.updated_at ?? null,
	};
};

export const RecommendedCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const context = params.context as DecisionTableContext;
	const rawRecommendedPercent = context.recommendedIncreasePercent;
	const isEffectivelyWhole = Math.abs(rawRecommendedPercent - Math.round(rawRecommendedPercent)) < 1e-9;
	const recommendedPercent = `${isEffectivelyWhole ? rawRecommendedPercent.toFixed(0) : rawRecommendedPercent.toFixed(2)}%`;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="semi-bold" color="green-5">
				{recommendedPercent}
			</Text>
		</div>
	);
};

export const EmployeeNameCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	if (!row) return null;
	const isExpanded = context.expandedRowId === row?._id;

	const handleToggle = () => {
		if (isExpanded) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};

	return (
		<div className="flex h-full items-center gap-2">
			<ButtonIcon
				variant="ghost"
				color="neutral"
				size={16}
				icon={isExpanded ? faCaretUp : faCaretDown}
				onClick={(e) => {
					e.stopPropagation();
					handleToggle();
				}}
				aria-label={isExpanded ? "Collapse Notes" : "Expand Notes"}
			/>
			<div className="flex min-w-0 flex-col justify-center gap-0.5">
				<Text
					size="paragraph-3"
					weight="regular"
					color="neutral-9"
					wrap={false}
					dangerouslySetClassName="truncate"
				>
					{row.employee_name}
				</Text>
				<Text size="caption" weight="regular" color="neutral-7">
					{row.employee_title}
				</Text>
			</div>
		</div>
	);
};

export const CurrentSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	if (!row) return null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3" weight="regular">
				{formatCurrencyFull(row.current_salary)}
			</Text>
		</div>
	);
};

export const IncreasePercentCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || !context) return null;

	const rawContextValue = context.increaseValues[row._id]?.percent;
	const contextValue: number | null = typeof rawContextValue === "number" ? rawContextValue : null;
	const rowStatus = row.status || "pending";

	const showValidationError =
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftPercentMissing(context.increaseValues, row._id);

	if (rowStatus !== "pending") {
		return row.proposed_increase_percent != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{toFixed(row.proposed_increase_percent, 2)}%
				</Text>
			</div>
		) : null;
	}

	return (
		<div className="flex h-full items-center gap-1">
			<InputNumeric
				id={`increase-percent-${row._id}`}
				data-testid={`increase-percent-${row._id}`}
				value={contextValue}
				allowEmpty={true}
				onChange={(val) => {
					if (val != null && contextValue != null) {
						const displayed = toFixed(contextValue, 2);
						const delta = toFixed(val - displayed, 2);
						if (Math.abs(Math.abs(delta) - 1) < 0.001) {
							context.onPercentChange(row._id, contextValue + delta);
							return;
						}
					}
					context.onPercentChange(row._id, val ?? "");
				}}
				onFocus={() => {
					if (contextValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
			<span className="text-sm text-neutral-500">%</span>
		</div>
	);
};

export const IncreaseAmountCellRenderer = (params: ICellRendererParams<DecisionRow>): React.ReactNode => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || !context) return null;

	const rawContextValue = context.increaseValues[row._id]?.amount;
	const contextValue: number | null = typeof rawContextValue === "number" ? toFixed(rawContextValue) : null;
	const rowStatus = row.status || "pending";

	const showValidationError =
		rowStatus === "pending" &&
		!row.superRow &&
		String(context.groupCannotBeSubmitted ?? "") === String(context.managerId) &&
		isDraftAmountMissing(context.increaseValues, row._id);

	if (rowStatus !== "pending") {
		return row.proposed_increase_amount != null ? (
			<div className="flex h-full items-center">
				<Text size="paragraph-3" weight="regular">
					{formatCurrencyFull(row.proposed_increase_amount)}
				</Text>
			</div>
		) : null;
	}

	return (
		<div className="flex h-full items-center gap-1">
			<span className="text-sm text-neutral-500">$</span>
			<InputNumeric
				id={`increase-amount-${row._id}`}
				data-testid={`increase-amount-${row._id}`}
				value={contextValue}
				allowEmpty={true}
				onChange={(val) => context.onAmountChange(row._id, val ?? "")}
				onFocus={() => {
					if (contextValue == null) {
						context.onPercentChange(row._id, context.recommendedIncreasePercent);
					}
				}}
				precision={2}
				step={1}
				placeholder="-"
				errored={showValidationError}
				dangerouslySetClassName="w-[82px]"
				size={"small"}
			/>
		</div>
	);
};

export const NewSalaryCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext;
	if (!row || !context) return null;
	const increaseAmount = context.increaseValues[row._id]?.amount;
	const hasValue = increaseAmount != null && increaseAmount !== "";
	const newSalary = hasValue ? row.current_salary + (increaseAmount as number) : null;
	return (
		<div className="flex h-full items-center">
			<Text size="paragraph-3">{newSalary != null ? formatCurrencyFull(newSalary) : "-"}</Text>
		</div>
	);
};

export const EmployeeInfoCellRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	if (!row || row.superRow) return null;

	const toggleNotesRow = () => {
		if (!context) return;
		if (context.expandedRowId === row._id) {
			context.onCollapse(row._id);
		} else {
			context.onExpand(row._id);
		}
	};

	const notesExpanded = context?.expandedRowId === row._id;
	const noteExists = Boolean(row?.notes);
	return (
		<div className="flex h-full items-center gap-2">
			<Tooltip content={`Notes about ${row.employee_name}`} placement="top">
				<ButtonIcon
					icon={faMemoPad}
					color={noteExists ? "primary" : "neutral"}
					onClick={(e) => {
						e.stopPropagation();
						toggleNotesRow();
					}}
					aria-expanded={notesExpanded}
					aria-label={`${notesExpanded ? "Close" : "Open"} notes for ${row.employee_name}`}
					size={20}
				/>
			</Tooltip>
			<Tooltip content={`${row.employee_name}'s Goals`} placement="top">
				<ButtonIcon
					color={"primary"}
					icon={faChartPieSimple}
					href={`/employees/${row.employee_id}/goals/`}
					aria-label={`${row.employee_name}'s Goals`}
					size={20}
				/>
			</Tooltip>
			<Tooltip content={`${row.employee_name}'s Reviews`} placement="top">
				<ButtonIcon
					color={"primary"}
					icon={faPenToSquare}
					href={`/reviews/${row.employee_id}/reviews-of-me/`}
					aria-label={`${row.employee_name}'s Reviews`}
					size={20}
				/>
			</Tooltip>
		</div>
	);
};

export const SuperRowRenderer = (params: ICellRendererParams<DecisionRow>) => {
	const row = params.data;
	const context = params.context as DecisionTableContext | undefined;
	const notesValuesRef = context?.notesValuesRef;
	const activeNoteEditorRef = context?.activeNoteEditorRef;
	/** Set on super rows to `employee_id` so it matches `notesByDecisionRowId` */
	const noteRowId = row?.parentRowId ?? "";
	const serverNotesText = row?.notes != null ? String(row.notes) : "";
	const hasServerNotes = serverNotesText.length > 0;
	const onNotesChange = context?.onNotesChange;
	const editorRef = useRef<TextEditorRef>(null);

	const [notesDraft, setNotesDraft] = useState(() => {
		if (noteRowId && notesValuesRef && noteRowId in notesValuesRef.current) {
			return notesValuesRef.current[noteRowId];
		}
		return serverNotesText;
	});

	// Row data from the grid is server-sourced; drafts live in `notesValuesRef`. After AG Grid
	// `refreshCells({ force: true })` (e.g. when increase fields change elsewhere), this cell remounts
	// and must rehydrate from the ref or autosave appears broken for non-first groups.
	useEffect(() => {
		if (!noteRowId || !notesValuesRef) {
			setNotesDraft(serverNotesText);
			return;
		}
		if (noteRowId in notesValuesRef.current) {
			setNotesDraft(notesValuesRef.current[noteRowId]);
			return;
		}
		setNotesDraft(serverNotesText);
	}, [serverNotesText, noteRowId, notesValuesRef]);

	// Restore focus when this component remounts while the user was actively editing.
	// When AG Grid refreshes the full-width row (e.g. after autosave triggers a re-render),
	// the component unmounts and remounts, causing the TextEditor to lose focus.
	useEffect(() => {
		if (activeNoteEditorRef?.current === noteRowId && noteRowId) {
			// Defer focus to next frame so the TipTap editor is fully initialized
			const raf = requestAnimationFrame(() => {
				editorRef.current?.focus();
			});
			return () => cancelAnimationFrame(raf);
		}
	}, [activeNoteEditorRef, noteRowId]);

	const handleNotesEditorChange = useCallback(
		(value: string) => {
			const val = value.slice(0, NOTES_MAX_LENGTH);
			setNotesDraft(val);
			if (activeNoteEditorRef && noteRowId) {
				activeNoteEditorRef.current = noteRowId;
			}
			if (noteRowId) onNotesChange?.(noteRowId, val);
		},
		[noteRowId, onNotesChange, activeNoteEditorRef]
	);

	const handleEditorBlur = useCallback(
		(event: FocusEvent) => {
			if (!activeNoteEditorRef || activeNoteEditorRef.current !== noteRowId) return;
			if (event.relatedTarget != null) {
				// Focus moved to a real element — user intentionally left the editor
				activeNoteEditorRef.current = null;
			} else {
				// relatedTarget is null: either AG Grid is remounting the row (focus will be
				// restored on remount) or the user clicked non-focusable space. Use a short
				// delay to let the remount happen; if it doesn't, clear the ref.
				setTimeout(() => {
					if (activeNoteEditorRef.current === noteRowId) {
						activeNoteEditorRef.current = null;
					}
				}, 100);
			}
		},
		[activeNoteEditorRef, noteRowId]
	);

	if (!row || !context) return null;
	return (
		<div
			className="flex flex-col gap-2 px-6"
			style={{ padding: "16px 24px" }}
			onKeyDown={(e) => e.stopPropagation()}
			onKeyUp={(e) => e.stopPropagation()}
		>
			<Text size="paragraph-3" weight="semi-bold">
				Notes
			</Text>
			<div className="flex flex-col gap-1">
				<TextEditor
					ref={editorRef}
					key={noteRowId}
					id={`${noteRowId}_notes`}
					onChange={handleNotesEditorChange}
					onBlur={handleEditorBlur}
					initialValue={notesDraft}
					placeholder={!hasServerNotes ? `Add notes about ${row.employee_name}` : undefined}
					characterLimit={NOTES_MAX_LENGTH}
					minHeight={140}
					readOnly={row.status !== "pending"}
					dangerouslySetClassName="h-[120px] w-[1216px] resize-none rounded border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:border-primary-5 focus:outline-none"
				/>
			</div>
		</div>
	);
};
